import {
  EventFilter,
  EventQuery,
  PersistedRuntimeEvent,
  ReplayWindow
} from '../contracts'

function matchesFilter(event: PersistedRuntimeEvent, filter: EventFilter = {}): boolean {
  if (filter.types && !filter.types.includes(event.type)) return false
  if (filter.categories && !filter.categories.includes(event.category)) return false
  if (filter.sources && !filter.sources.includes(event.source)) return false
  if (filter.priorities && !filter.priorities.includes(event.severity)) return false
  if (filter.streams && !filter.streams.includes(event.stream)) return false

  if (filter.executionId && event.context.executionId !== filter.executionId) return false
  if (filter.correlationId && event.context.correlationId !== filter.correlationId) return false
  if (filter.actor && event.context.actor !== filter.actor) return false
  if (filter.route && event.context.route !== filter.route) return false
  if (filter.service && event.context.service !== filter.service) return false
  if (filter.domain && event.context.domain !== filter.domain) return false

  if (filter.afterSequence && event.sequence <= filter.afterSequence) return false
  if (filter.from && event.timestamp < filter.from) return false
  if (filter.to && event.timestamp > filter.to) return false
  if (filter.customFilter && !filter.customFilter(event)) return false

  return true
}

export class RuntimeReplayService {
  static query(events: PersistedRuntimeEvent[], query: EventQuery): PersistedRuntimeEvent[] {
    const { filter = {}, limit = 100, offset = 0, sortBy = 'sequence', sortOrder = 'desc' } = query

    const filtered = events.filter((event) => matchesFilter(event, filter))

    const sorted = filtered.sort((a, b) => {
      const direction = sortOrder === 'asc' ? 1 : -1
      switch (sortBy) {
        case 'timestamp':
          return (a.timestamp.getTime() - b.timestamp.getTime()) * direction
        case 'type':
          return a.type.localeCompare(b.type) * direction
        case 'category':
          return a.category.localeCompare(b.category) * direction
        case 'priority':
          return a.severity.localeCompare(b.severity) * direction
        case 'sequence':
        default:
          return (a.sequence - b.sequence) * direction
      }
    })

    return sorted.slice(offset, offset + limit)
  }

  static replayWindow(events: PersistedRuntimeEvent[], filter: EventFilter = {}, window: ReplayWindow = {}): PersistedRuntimeEvent[] {
    return this.query(events, {
      filter: {
        ...filter,
        from: window.from,
        to: window.to,
        afterSequence: window.afterSequence
      },
      limit: window.maxEvents || 200,
      sortBy: 'sequence',
      sortOrder: 'asc'
    })
  }

  static replayByExecution(events: PersistedRuntimeEvent[], executionId: string, window: ReplayWindow = {}): PersistedRuntimeEvent[] {
    return this.replayWindow(events, { executionId }, window)
  }

  static replayByCorrelation(events: PersistedRuntimeEvent[], correlationId: string, window: ReplayWindow = {}): PersistedRuntimeEvent[] {
    return this.replayWindow(events, { correlationId }, window)
  }
}